// Runner bodies painted directly at their on-screen size, 30x48 hd px (24 world px tall), instead of being drawn at
// 40x64 and resampled (character-art.ts shrink), which votes away 1 px details. characterBodyCanvas uses this at game
// size; proportions, anchor and the arm/weapon rig are the production ones.
// Extra detail (option 3): a four-tone jacket ramp, crew-colour trim, belt/holster/gloves, readable mask eyes,
// three-tone hair, and an optional neon rim light (pink from the left, cyan from the right) on the silhouette.
import { canvas } from './art';
import type { Look } from './art';
import { K, px, dk, lt, mix } from './hd-kit';
import { CREW } from './crew';
import type { Facing } from './character-motion';

export const RD_W = 30, RD_H = 48;
type G = CanvasRenderingContext2D;

export function poly(g: G, pts: number[][], color: string) {
  const ys = pts.map(p => p[1]), y0 = Math.ceil(Math.min(...ys)), y1 = Math.ceil(Math.max(...ys));
  for (let y = y0; y < y1; y++) {
    const cuts: number[] = [];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], q = pts[(i + 1) % pts.length];
      if ((p[1] <= y + 0.5 && q[1] > y + 0.5) || (q[1] <= y + 0.5 && p[1] > y + 0.5)) cuts.push(p[0] + (y + 0.5 - p[1]) / (q[1] - p[1]) * (q[0] - p[0]));
    }
    cuts.sort((a, b) => a - b);
    for (let i = 0; i + 1 < cuts.length; i += 2) { const x = Math.round(cuts[i]); px(g, x, y, Math.round(cuts[i + 1]) - x, 1, color); }
  }
}
export function outline(src: HTMLCanvasElement): HTMLCanvasElement {
  const [c, g] = canvas(src.width, src.height), [m, mg] = canvas(src.width, src.height);
  mg.drawImage(src, 0, 0); mg.globalCompositeOperation = 'source-in'; mg.fillStyle = K.ink; mg.fillRect(0, 0, src.width, src.height);
  for (const [x, y] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) g.drawImage(m, x, y);
  g.drawImage(src, 0, 0); return c;
}
const rgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
/** Neon rim: tint the outermost opaque pixel on each side toward the light coming from that side. */
function rimLight(c: HTMLCanvasElement, left: string, right: string, k: number) {
  const g = c.getContext('2d')!, d = g.getImageData(0, 0, c.width, c.height), a = d.data, w = c.width, L = rgb(left), R = rgb(right);
  const src = new Uint8ClampedArray(a);
  for (let y = 0; y < c.height; y++) for (let x = 0; x < w; x++) {
    const i = (y * w + x) * 4; if (src[i + 3] < 128) continue;
    const open = (dx: number) => { const xx = x + dx; return xx < 0 || xx >= w || src[(y * w + xx) * 4 + 3] < 128; };
    const t = open(-1) ? L : open(1) ? R : null; if (!t) continue;
    for (let j = 0; j < 3; j++) a[i + j] = a[i + j] + (t[j] - a[i + j]) * k;
  }
  g.putImageData(d, 0, 0);
}

export interface DetailOpts { rim?: boolean; bob?: number; frame?: number; travel?: number; extra?: (g: G) => void }
export function detailedBodyCanvas(l: Look, view: Facing, o: DetailOpts = {}): HTMLCanvasElement {
  const west = view === 'W' || view === 'NW' || view === 'SW';
  // paint the east-facing variants and mirror for west, so asymmetric detail stays consistent
  const v: Facing = west ? (view === 'W' ? 'E' : view === 'NW' ? 'NE' : 'SE') : view;
  const profile = v === 'E', back = v === 'N' || v === 'NE', diagonal = v === 'SE' || v === 'NE';
  const [c, g] = canvas(RD_W, RD_H), cx = 15, bob = o.bob ?? 0;
  const crew = l.crew, mark = crew !== undefined ? (CREW[crew]?.color ?? CREW[0].color) : lt(l.jacket.slice(0, 7), 0.25);
  const jk = l.jacket.slice(0, 7), hi = lt(jk, 0.3), mid = jk, lo = dk(jk, 0.32), deep = dk(jk, 0.62);
  const pants = mix(dk(jk, 0.6), K.slate, 0.35), pantsHi = lt(pants, 0.16), pantsLo = dk(pants, 0.3);
  const broad = crew === 1 || l.extra === 'bulky' || l.extra === 'helmet';
  const lean = profile || diagonal ? 1 : 0;
  const tw = profile ? 9 : broad ? 15 : diagonal ? 12 : 14, tx = cx - Math.floor(tw / 2) + lean;

  // legs: thigh, knee pad, shin, three-tone boot with a lit toe cap and a sole line. Feet follow movement (two planted
  // contacts per cycle) independently of where the body faces, like the production rig (character-art.ts leg).
  const frame = o.frame ?? 0, cycle = frame ? (frame - 1) / 8 * Math.PI * 2 : 0;
  let ta = o.travel ?? Math.PI / 2; if (west) ta = Math.PI - ta; // the canvas is mirrored afterwards
  const seg = (a: { x: number; y: number }, b: { x: number; y: number }, w: number, col: string) => {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
    for (let i = 0; i <= n; i++) px(g, Math.round(a.x + (b.x - a.x) * i / n - w / 2), Math.round(a.y + (b.y - a.y) * i / n), w, 1, col);
  };
  const leg = (side: number, far: boolean) => {
    const phase = cycle + (side < 0 ? Math.PI : 0), stride = frame ? Math.sin(phase) : 0, lift = frame ? Math.max(0, Math.cos(phase)) * 2 : 0;
    const col = far ? dk(pants, 0.22) : pants;
    const hip = { x: cx + (profile ? side : side * 2.5), y: 33 };
    const foot = { x: cx + (profile ? side : side * 3) + Math.cos(ta) * stride * 3.5, y: 46 + Math.sin(ta) * stride * 1.5 - lift };
    const knee = { x: hip.x + (foot.x - hip.x) * 0.5 + side * 0.5, y: 39 - lift * 0.4 };
    seg(hip, knee, 4, col); seg(hip, knee, 1, far ? col : pantsHi);
    seg(knee, { x: foot.x, y: foot.y - 2 }, 3, col);
    const kx = Math.round(knee.x - 2), ky = Math.round(knee.y - 1);
    px(g, kx, ky, 4, 3, far ? dk(K.metal, 0.3) : K.metal); px(g, kx + 1, ky, 2, 1, far ? K.metalLo : K.metalHi); px(g, kx, ky + 2, 4, 1, K.metalLo);
    const fw = profile ? 6 : 5, fx = Math.round(foot.x - (profile ? 2 : fw / 2)), fy = Math.round(foot.y - 3);
    px(g, fx, fy, fw, 3, dk(K.slate, 0.35)); px(g, fx, fy, fw, 1, dk(K.slate, 0.1));
    px(g, profile ? fx + fw - 2 : fx + 1, fy + 1, 2, 1, far ? K.metalLo : K.metalHi); px(g, fx, fy + 2, fw, 1, K.ink);
  };
  leg(profile ? -1 : -1, true); leg(1, false);
  // hips and belt: dark strap, buckle with a crew LED, a holster grip on the near hip
  px(g, tx + 1, 31 + bob, tw - 2, 3, pants); px(g, tx + 1, 31 + bob, tw - 2, 1, K.ink);
  if (!back) { px(g, cx - 1 + lean, 31 + bob, 3, 1, K.metalHi); px(g, cx + lean, 31 + bob, 1, 1, mark); }
  px(g, tx + tw - 2, 31 + bob, 2, 3, K.metalLo); px(g, tx + tw - 2, 31 + bob, 1, 1, K.metalHi);

  // Karasu's split coat tails
  if (crew === 3) {
    poly(g, [[tx, 28 + bob], [cx, 29 + bob], [cx - 1, 39], [tx - 1, 37]], deep);
    poly(g, [[cx + 1, 29 + bob], [tx + tw, 28 + bob], [tx + tw + 1, 37], [cx + 2, 39]], lo);
    px(g, tx - 1, 36, 1, 1, mark); px(g, tx + tw, 36, 1, 1, mark);
  }
  // jacket: shoulder line, four tones (lit left edge, base, shaded right, deep hem), crew trim at the collar and hem
  poly(g, [[tx + 2, 17 + bob], [tx + tw - 2, 17 + bob], [tx + tw + 1, 20 + bob], [tx + tw, 31 + bob], [tx, 31 + bob], [tx - 1, 20 + bob]], mid);
  px(g, tx, 20 + bob, 1, 10, hi); px(g, tx + 1, 19 + bob, 1, 3, hi);
  px(g, tx + tw - 2, 20 + bob, 2, 11, lo); px(g, tx + tw - 3, 23 + bob, 1, 7, lo);
  px(g, tx + 1, 29 + bob, tw - 2, 2, deep); px(g, tx + 1, 30 + bob, tw - 2, 1, dk(mark, 0.45));
  px(g, tx + 2, 18 + bob, tw - 4, 1, hi); // shoulder seam catching the light
  if (broad) { px(g, tx, 18 + bob, 3, 2, K.metal); px(g, tx + tw - 3, 18 + bob, 3, 2, K.metalLo); px(g, tx, 18 + bob, 2, 1, K.metalHi); }
  // raised collar with crew trim, neck shadow
  poly(g, [[cx - 4 + lean, 16 + bob], [cx + 4 + lean, 16 + bob], [cx + 3 + lean, 20 + bob], [cx + lean, 21 + bob], [cx - 3 + lean, 20 + bob]], deep);
  px(g, cx - 4 + lean, 16 + bob, 1, 3, mark); if (!profile) px(g, cx + 4 + lean, 16 + bob, 1, 3, dk(mark, 0.3));
  px(g, cx - 1 + lean, 15 + bob, 3, 2, dk(l.skin, 0.3));
  if (back) {
    // back panel, crew diamond with a lit centre, a vent seam
    px(g, tx + 2, 21 + bob, tw - 4, 7, lo); px(g, tx + 2, 21 + bob, tw - 4, 1, hi);
    poly(g, [[cx + lean, 21 + bob], [cx + 3 + lean, 24 + bob], [cx + lean, 27 + bob], [cx - 3 + lean, 24 + bob]], dk(mark, 0.2));
    px(g, cx + lean, 23 + bob, 1, 3, lt(mark, 0.5)); px(g, cx + lean, 28 + bob, 1, 2, deep);
  } else {
    const zip = cx + (diagonal ? 2 : profile ? 3 : 0);
    px(g, zip, 21 + bob, 1, 9, deep); px(g, zip + 1, 22 + bob, 1, 7, K.metal); px(g, zip + 1, 21 + bob, 1, 1, K.white);
    if (!profile) {
      // chest rig: pouch with a lit flap and a snap, a crew-coloured strap tab, a diagonal sling
      px(g, tx + 2, 23 + bob, 3, 3, lo); px(g, tx + 2, 23 + bob, 3, 1, hi); px(g, tx + 3, 24 + bob, 1, 1, K.metalHi);
      px(g, tx + tw - 5, 22 + bob, 2, 4, deep); px(g, tx + tw - 5, 22 + bob, 2, 1, mark);
      for (let i = 0; i < 8; i++) px(g, tx + tw - 3 - i, 20 + bob + i, 1, 1, i % 3 === 0 ? K.metalLo : deep);
    } else {
      px(g, tx + 1, 23 + bob, 3, 4, lo); px(g, tx + 1, 23 + bob, 3, 1, hi); // side pocket
    }
  }

  // head: jaw with a cheek shadow, ear, three-tone hair with a lit strand
  const hx = cx - 5 + lean, hy = 4 + bob;
  poly(g, [[hx + 2, hy], [hx + 8, hy], [hx + 10, hy + 2], [hx + 10, hy + 8], [hx + 7, hy + 11], [hx + 3, hy + 11], [hx, hy + 8], [hx, hy + 2]], l.skin);
  px(g, hx + 8, hy + 3, 2, 6, dk(l.skin, 0.22)); px(g, hx + 2, hy + 9, 6, 1, dk(l.skin, 0.15));
  const hair = l.hair, hairHi = lt(hair, 0.28), hairLo = dk(hair, 0.3);
  poly(g, [[hx, hy + 3], [hx, hy + 1], [hx + 2, hy - 1], [hx + 8, hy - 1], [hx + 10, hy + 1], [hx + 10, hy + 4], [hx + 7, hy + 2], [hx + 3, hy + 3]], hair);
  px(g, hx + 3, hy - 1, 4, 1, hairHi); px(g, hx + 2, hy, 2, 1, hairHi); px(g, hx + 8, hy + 1, 2, 2, hairLo);
  if (back) { px(g, hx + 1, hy + 3, 8, 6, hair); px(g, hx + 2, hy + 3, 2, 4, hairHi); px(g, hx + 2, hy + 8, 6, 2, hairLo); }
  else if (profile) { px(g, hx + 1, hy + 3, 3, 6, hair); px(g, hx + 1, hy + 3, 1, 4, hairHi); px(g, hx + 4, hy + 5, 1, 2, dk(l.skin, 0.35)); }
  else { px(g, hx, hy + 3, 1, 5, hair); px(g, hx + 9, hy + 3, 1, 4, hairLo); px(g, hx + 3, hy + 5, 1, 1, K.ink); px(g, hx + 6, hy + 5, 1, 1, K.ink); px(g, hx + 4, hy + 8, 2, 1, dk(l.skin, 0.45)); }
  mask(g, l, v, hx, hy, mark);
  if (o.rim) rimLight(c, K.pink, K.cyan, 0.42);
  let out = outline(c);
  if (west) { const [m, mg] = canvas(RD_W, RD_H); mg.translate(RD_W, 0); mg.scale(-1, 1); mg.drawImage(out, 0, 0); out = m; }
  if (o.extra) o.extra(out.getContext('2d')!); // e.g. a downed runner's resting arms (they carry their own ink edge)
  return out;
}

/** Crew masks at native size: a lit shell, white eyes under crew-colour LED lids, each crew's own marks. */
function mask(g: G, l: Look, v: Facing, hx: number, hy: number, mark: string) {
  const back = v === 'N' || v === 'NE', side = v === 'E';
  if (l.crew === undefined) return;
  const crew = l.crew, shell = lt(K.slate, 0.12), hi = lt(shell, 0.28), lo = dk(shell, 0.4);
  if (crew === 1 || crew === 2) for (const ex of [hx, hx + 8]) { // horns (Oni) or ears (Kitsune)
    poly(g, [[ex, hy + 2], [ex - (crew === 1 ? 1 : 0), hy - 3], [ex + 2, hy], [ex + 2, hy + 2]], shell);
    px(g, ex, hy - 2, 1, 2, crew === 1 ? mark : hi);
  }
  if (back) { px(g, hx - 1, hy + 6, 12, 1, lo); px(g, hx + 3, hy + 6, 3, 1, dk(mark, 0.3)); return; }
  const mx = side ? hx + 4 : v === 'SE' ? hx + 1 : hx, mw = side ? 6 : 10;
  poly(g, [[mx + 1, hy + 2], [mx + mw - 1, hy + 2], [mx + mw, hy + 4], [mx + mw - 1, hy + 9], [mx + Math.floor(mw / 2), hy + 12], [mx + 1, hy + 9], [mx, hy + 4]], shell);
  px(g, mx + 1, hy + 3, mw - 3, 1, hi); px(g, mx + mw - 2, hy + 4, 1, 5, lo); px(g, mx + 2, hy + 10, mw - 4, 1, lo);
  if (side) {
    px(g, mx + 2, hy + 4, 3, 1, mark); px(g, mx + 3, hy + 5, 2, 1, K.white); px(g, mx + 2, hy + 8, 2, 1, mark);
    if (crew === 3) beak(g, mx + 5, hy, mark, true);
    return;
  }
  px(g, mx + 1, hy + 4, 3, 1, mark); px(g, mx + mw - 4, hy + 4, 3, 1, mark);
  px(g, mx + 2, hy + 5, 2, 1, K.white); px(g, mx + mw - 4, hy + 5, 2, 1, K.white);
  const m2 = Math.floor(mw / 2);
  if (crew === 0) { px(g, mx + 1, hy + 7, 2, 1, mark); px(g, mx + mw - 3, hy + 7, 2, 1, mark); px(g, mx + m2 - 1, hy + 9, 3, 1, mark); }
  if (crew === 1) { px(g, mx + 2, hy + 7, mw - 4, 1, mark); px(g, mx + 2, hy + 8, 1, 2, K.white); px(g, mx + mw - 3, hy + 8, 1, 2, K.white); }
  if (crew === 2) { px(g, mx + m2 - 1, hy + 2, 2, 2, mark); px(g, mx + 1, hy + 7, 1, 1, mark); px(g, mx + mw - 2, hy + 7, 1, 1, mark); px(g, mx + m2 - 1, hy + 9, 2, 1, mark); }
  if (crew === 3) beak(g, mx + m2 + (v === 'SE' ? 1 : 0), hy, mark, false);
}

/** Karasu-tengu's beak: a wedge standing off the mask, a lit top plane over a shaded underside, an amber ridge and a
 *  dark tip, so it reads as a solid shape (not a line) at 30x48. Front: projects down; side: projects forward (east). */
function beak(g: G, x: number, hy: number, mark: string, side: boolean) {
  const lit = lt(mark, 0.25), plane = dk(mark, 0.5), under = dk(mark, 0.78);
  if (side) {
    poly(g, [[x, hy + 5], [x + 4, hy + 7], [x + 6, hy + 9], [x, hy + 10]], plane);
    poly(g, [[x, hy + 8], [x + 6, hy + 9], [x, hy + 10]], under);
    px(g, x, hy + 5, 4, 1, lit); px(g, x + 1, hy + 6, 4, 1, mark); px(g, x + 3, hy + 7, 2, 1, lit); px(g, x + 5, hy + 8, 1, 1, K.ink);
    return;
  }
  px(g, x - 2, hy + 6, 5, 2, mark); px(g, x - 2, hy + 6, 5, 1, lit); // lit top plane across the root
  px(g, x - 1, hy + 8, 3, 3, plane); px(g, x - 1, hy + 8, 1, 3, mark); px(g, x - 1, hy + 8, 1, 1, lit); // left face lit, right face shaded
  px(g, x, hy + 11, 2, 1, under); px(g, x, hy + 12, 1, 1, K.ink); // shaded point
}